import { Stack, Typography } from '@mui/material'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useGetResourceCategories } from 'hooks/api/useGetResourceCategories'
import { useGetResources } from 'hooks/api/useGetResources'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { InfoCenterHeader } from './InfoCenterHeader'
import { ResourceCard } from './ResourceCard'
import { type Resource, type ResourceCategory } from 'src/types/api'

const noneCategoryName = 'none'

export const ResourcesPage = () => {
  const useGetResourceCategoriesQuery = useGetResourceCategories()
  const useGetResourcesQuery = useGetResources()

  const filteredAndSortedResources = [
    ...(useGetResourcesQuery.data ?? []),
  ].sort((a, b) => {
    const hasDisplayOrderA =
      a.displayOrder !== null && a.displayOrder !== undefined
    const hasDisplayOrderB =
      b.displayOrder !== null && b.displayOrder !== undefined

    if (hasDisplayOrderA && hasDisplayOrderB) {
      if (a.displayOrder !== b.displayOrder) {
        return (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
      }
    }

    if (hasDisplayOrderA !== hasDisplayOrderB) {
      return hasDisplayOrderA ? -1 : 1
    }

    const dateA = new Date(a.createdOn ?? 0)
    const dateB = new Date(b.createdOn ?? 0)
    return dateB.getTime() - dateA.getTime()
  })

  const sortedCategories = [...(useGetResourceCategoriesQuery.data ?? [])]
    .filter(
      (category) => category.name.trim().toLowerCase() !== noneCategoryName
    )
    .sort((a, b) => {
      const hasDisplayOrderA =
        a.displayOrder !== null && a.displayOrder !== undefined
      const hasDisplayOrderB =
        b.displayOrder !== null && b.displayOrder !== undefined

      if (hasDisplayOrderA && hasDisplayOrderB) {
        if (a.displayOrder !== b.displayOrder) {
          return (a.displayOrder ?? 0) - (b.displayOrder ?? 0)
        }
      }

      if (hasDisplayOrderA !== hasDisplayOrderB) {
        return hasDisplayOrderA ? -1 : 1
      }

      return a.name.localeCompare(b.name)
    })

  const resourcesByCategoryId = filteredAndSortedResources.reduce<
    Record<number, Resource[]>
  >((accumulator, resource) => {
    if (
      !(resource.fileName ?? resource.url) ||
      resource.resourceCategoryId === undefined
    ) {
      return accumulator
    }

    accumulator[resource.resourceCategoryId] = [
      ...(accumulator[resource.resourceCategoryId] ?? []),
      resource,
    ]

    return accumulator
  }, {})

  const visibleCategories = sortedCategories.filter(
    (category) => (resourcesByCategoryId[category.id] ?? []).length > 0
  )

  if (
    useGetResourcesQuery.isPending ||
    useGetResourceCategoriesQuery.isPending
  ) {
    return (
      <InfoCenterHeader currentTab={'/InfoCenter/ResourcesPage'}>
        <ActivityIndicator />
      </InfoCenterHeader>
    )
  }

  return (
    <InfoCenterHeader currentTab={'/InfoCenter/ResourcesPage'}>
      <Stack spacing={2}>
        {visibleCategories.map((category: ResourceCategory) => {
          const resources = resourcesByCategoryId[category.id] ?? []

          return (
            <Accordion key={category.id} defaultExpanded={false}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h3">{category.name}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {resources.map((resource) => (
                    <ResourceCard key={resource.id} resource={resource} />
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          )
        })}
      </Stack>
    </InfoCenterHeader>
  )
}
